import { useEffect, useState } from 'react'

import { Loader, LoaderOptions } from '@googlemaps/js-api-loader'

type UsePlacesServiceArgs = {
  apiKey?: string
  apiOptions?: Partial<LoaderOptions>
  onLoadFailed?: (error: Error) => void
}

type UsePlacesServiceRes = {
  autocompleteService?: google.maps.places.AutocompleteService
  placesService?: google.maps.places.PlacesService
  sessionToken?: google.maps.places.AutocompleteSessionToken
}

const isTestEnv = process.env['NODE_ENV'] === 'test'
const predictions = [
  {
    description: '1010 Test St., Test City, CA 90210',
    place_id: 'place-id',
    matched_substrings: [],
    structured_formatting: { main_text: 'test', secondary_text: 'test', main_text_matched_substrings: [] },
    terms: [],
    types: [],
  },
]
const place = {
  name: '1010 Test St.',
  address_components: [
    { short_name: 'Test City ', types: ['locality'], long_name: 'Test City' },
    { short_name: '90210', types: ['postal_code'], long_name: '94016' },
    { short_name: 'CA', types: ['administrative_area_level_1'], long_name: 'California' },
  ],
}
const testUseGooglePlaces = {
  autocompleteService: {
    getPlacePredictions: async () => Promise.resolve({ predictions }),
    getQueryPredictions: () => null,
  },
  placesService: {
    getDetails: (_, callback) => callback(place, 'OK' as google.maps.places.PlacesServiceStatus.OK),
  } as google.maps.places.PlacesService,
  sessionToken: {},
}

export const useGooglePlaces = ({ apiKey, apiOptions, onLoadFailed }: UsePlacesServiceArgs): UsePlacesServiceRes => {
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | undefined>(
    undefined
  )
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | undefined>(undefined)
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | undefined>(undefined)

  const initializeService = () => {
    if (!window?.google?.maps?.places) throw new Error('Google maps places script not loaded')
    setAutocompleteService(new window.google.maps.places.AutocompleteService())
    setPlacesService(new window.google.maps.places.PlacesService(document.createElement('div')))
    setSessionToken(new google.maps.places.AutocompleteSessionToken())
  }

  useEffect(() => {
    const init = async () => {
      if (!apiKey) return

      try {
        if (!window?.google?.maps?.places) {
          await new Loader({ apiKey, ...apiOptions }).importLibrary('places')
        }
        initializeService()
      } catch (error) {
        if (typeof onLoadFailed === 'function') onLoadFailed(error as Error)
      }
    }

    if (apiKey) {
      init()
    } else {
      initializeService()
    }
  }, [apiKey, apiOptions, onLoadFailed])

  return isTestEnv ? testUseGooglePlaces : { autocompleteService, placesService, sessionToken }
}
